import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  OnDestroy,
  computed,
  effect,
  input,
} from "@angular/core";
import { delay, fromEvent, Subject } from "rxjs";
import { debounceTime, throttleTime, takeUntil } from 'rxjs/operators';
import * as _ from 'lodash';
import { Column, UserColumn, InternalColumn, ColumnType, ColumnSortDirection, ColumnSortEvent, TableStyleConfig, SelectedAction } from '../data-table.interfaces';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MenuItem } from '@gridscale/primeng17/api';
import { UiTranslateService } from '../../../helper/lang/ui-translate.service';
import { Button, OverlayAlertComponent } from '../../overlay-alert/overlay-alert.component';
import { HighlightSearchPipe } from '../../../helper/pipes/highlight-search.pipe';
import { TriStateCheckboxChangeEvent } from "@gridscale/primeng17/tristatecheckbox";

@Component({
  selector: 'internal-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [HighlightSearchPipe],
  standalone: false
})
export class InternalTableComponent<T extends object> implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  protected readonly destroyed$ = new Subject<boolean>();

  /**
   * incoming table styles configuration that overwrites the default
   */
  tableStyleConfig = input<TableStyleConfig | undefined>(undefined);

  /**
   * table has a helper cell at the end of each row (used for adding various effects to the row, like the loading bar for example)
   */
  @Input() hasHelperCell: boolean = false;

  /**
   * no hover effect on the rows
   */
  @Input() jsHoverDisabled: boolean = false;

  /**
   * all the columns available
   */
  @Input() columns?: Column[];

  /**
   * the data that should be displayed in the rows. The keys are accessed by `dataSrc` attribute of the ´columns` definition
   */
  @Input() data: T[] = [];

  /**
   * the data that is in a busy state
   */
  @Input() busyRows?: T[] = [];

  /**
   * the user specific columns; stores the visibility and order of the table columns
   * That should come from a user store, and should be updated on `changeColumns`
   */
  @Input() userColumns?: UserColumn[];

  /**
   * total amount of (existing) data rows
   */
  @Input() total = 0;

  /**
   * the column `id` which to sort after
   */
  @Input() sort?: string;

  /**
   * the sort direction
   */
  @Input() sortDirection: ColumnSortDirection | string = ColumnSortDirection.ASC;

  /**
   * whether the rows should be selectable or not
   */
  @Input() selectable: boolean = true;

  /**
   * whether multiple selection is allowed or not (checkbox vs. radiobutton)
   */
  @Input() multiSelect: boolean = true;

  /**
   * whether select all via header is allowed or not (only if multiselect is true)
   */
  @Input() allSelect: boolean = false;

  /**
   * a primary (unique) key within the data objects (defaults to 'objectUuid')
   */
  @Input() dataPrimaryKey = 'object_uuid';

  /**
   * indicates if data is loading
   */
  @Input() loading?: boolean;

  /**
   * indicates if next bunch of data is loading
   */
  @Input() loadingNext?: boolean;

  /**
   * container to which the fixed header will be sticky
   */
  @Input() viewportElement?: string = '.viewport';

  /**
   * offset in pixels for fixed header top position
   */
  @Input() fixedHeaderOffset?: number = 0;

  /**
   * whether the table has a fixed/sticky header or not
   */
  @Input() fixedHeader?= false;

  /**
   * whether the table has a fixed/sticky first column or not
   */
  @Input() fixedFirstColumn?= false;

  /**
   * whether the table has a fixed/sticky last column or not
   */
  @Input() fixedLastColumn?= false;

  /**
   * the selected rows (rows must match row in `data`)
   */
  @Input() selection?: T[];

  @Input() search?: string;
  @Input() searchBy?: string;


  /**
   * An array of rows disabled for (de-)selection (rows must match row in `data`)
   */
  @Input() disabledSelection?: T[];

  /**
   * whether ...
   */
  @Input() showFavorite: boolean = true;

  /**
   * whether ...
   */
  @Input() showMakeFavAction: boolean = true;

  /**
   * the actions available for the selected rows
   */
  @Input() selectedActions?: SelectedAction[];

  /**
   * disables the uuid copy context menu
   */
  @Input() disableContextMenu: boolean = false;

  /**
   * is emitted when the next bunch of data should be fetched from source
   */
  @Output() loadNext: EventEmitter<void> = new EventEmitter();

  /**
   * is emitted everytime the selected rows change
   */
  @Output() selectionChange: EventEmitter<T[]> = new EventEmitter();

  /**
   * is emitted after the sorting changed (click on column header)
   */
  @Output() changeSort: EventEmitter<ColumnSortEvent> = new EventEmitter();

  /**
   * is emitted after the arrangement of visible columns have changed.
   */
  @Output() changeColumns: EventEmitter<UserColumn[]> = new EventEmitter();

  /**
   * is emitted when a cell is clicked (column `type` === ColumnType.LINK)
   */
  @Output() cellClick: EventEmitter<{ col: Column; row: T }> = new EventEmitter();

  /**
   * is emitted ...
   */
  @Output() storeFavorites = new EventEmitter();

  /**
   * is emitted ...
   */
  @Output() storeUnFavorites = new EventEmitter();

  /**
   * the first wrapper for the table; holds the hover container and the selected actions; both overflow the container so css overflow on the container should be visible
   * @ignore
   */
  @ViewChild('igDataTableWrapperLevel1', { static: true }) wrapperLevel1?: ElementRef;

  /**
   * the second wrapper of the table; holds the vertical shadows and the main table; for both of them overflow needs to be hidden
   * @ignore
   */
  @ViewChild('igDataTableWrapperLevel2', { static: true }) wrapperLevel2?: ElementRef;


  /**
   * the third wrapper of the table; required for the functionality in the modal ready mode
   * @ignore
   */
  @ViewChild('igDataTableWrapperLevel3', { static: true }) wrapperLevel3?: ElementRef;


  /**
   * the main table
   * @ignore
   */
  @ViewChild('igDataTable', { static: true }) theTable?: ElementRef<HTMLTableElement>;

  /**
   * the fixed/sticky header
   * @ignore
   */
  @ViewChild('igStickyHeader', { static: true }) theHeader?: ElementRef;
  @ViewChild('selectedActionsContainer', { static: true }) selectedActionsContainer?: ElementRef;

  /**
   * the table configuration modal
   * @ignore
   */
  @ViewChild('settingsOverlay', { static: true }) settingsOverlay?: OverlayAlertComponent;

  /**
   * the left shadow container for horizontal scrolling
   * @ignore
   */
  @ViewChild('igDataTableLeftShadow', { static: true }) theLeftShadow?: ElementRef;

  /**
   * the right shadow container for horizontal scrolling
   * @ignore
   */
  @ViewChild('igDataTableRightShadow', { static: true }) theRightShadow?: ElementRef;

  /**
   * @ignore
   */
  items: MenuItem[] = [{
    label: this.translate.instant('copy_uuid'), command: () => {
      if (this.selectedContextMenuRowObjectUuid !== null) {
        navigator.clipboard.writeText(this.selectedContextMenuRowObjectUuid)
      }
    }
  }]

  protected selectedContextMenuRowObjectUuid: string | null = null;

  theColumns: InternalColumn[] = [];
  selectedRows: string[] = [];
  selectedRow?: string;

  dataChange$ = new Subject<void>();
  fixedTableHeader: boolean = false;
  fixedBottomTableHeader: boolean = false;
  selectAll: boolean|null = null;

  fixedHeaderHeight = 0;
  fixedHeaderOffsetTop = 0;
  selectedActionsContainerHeight = 0;

  visibilityStep: number = 10;
  visibilityStartIndex: number = 0;
  visibilityEndIndex: number = 19;

  tableWidth: number = -1;
  tableShadowLeft: boolean = false;
  tableShadowRight: boolean = false;
  horizontalScrolling: boolean = false;
  tableGrid: boolean = false;

  pointerOverTable: boolean = false;
  rowHoverDisabled: boolean = false;
  hoverContainerTopOffset: number = 0;
  hoverContainerHeight: number = 0;
  hoverTimeout?: number;

  styleConfig = computed<TableStyleConfig>(() => {
    return {
      "rowGap": 10,
      "rowHeight": 50,
      "cellPaddingX": 15,
      "cellPaddingY": 5,
      "cellBackground": undefined,
      "lineHeight": 22,
      "backgroundColour": 'transparent',
      "tableHeight": 'auto',
      "fluidTableHeight": false,
      "whiteHeader": false,
      "surroundingBorder": false,
      ...(this.tableStyleConfig() || {})
    }
  });


  settingsOverlayButtons: Button[] = [
    {
      label: this.translate.instant('TABLE_GEN_3.SETTINGS_MODAL.CONFIRM'),
      mode: 'primary',
      keyShortcut: 'Escape',
      callback: (button, alert) => {
        alert.close();
      }
    }
  ];

  private readonly highlightPipe = inject(HighlightSearchPipe);


  constructor(private translate: UiTranslateService, private ref: ChangeDetectorRef, private renderer: Renderer2) {

    effect(() => {
      if (this.styleConfig().lineHeight) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-line-height", this.styleConfig().lineHeight + 'px');
      }
      if (this.styleConfig().rowGap) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-row-gap", this.styleConfig().rowGap + 'px');
      }
      if (this.styleConfig().rowHeight) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-row-height", this.styleConfig().rowHeight + 'px');
      }
      if (this.styleConfig().cellPaddingX || this.styleConfig().cellPaddingY) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-cell-padding", (this.styleConfig().cellPaddingY ?? 0) + 'px ' + (this.styleConfig().cellPaddingX ?? 0) + 'px');
      }
      if (this.styleConfig().cellPaddingX) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-selected-offset", this.styleConfig().cellPaddingX + 'px');
      }
      if (this.styleConfig().cellBackground) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-cell-background", this.styleConfig().cellBackground);
      }
      if (this.styleConfig().tableHeight && this.styleConfig().tableHeight !== 'auto') {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-height", this.styleConfig().tableHeight);
      }
      if (this.styleConfig().backgroundColour) {
        this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-background", this.styleConfig().backgroundColour);
      }
    })

    this.selectionChange.pipe(takeUntil(this.destroyed$), delay(25)).subscribe(() => {
      this.setFixedHeaderDetails();
      this.setFixedHeaderPosition();
      // this.ref.markForCheck();
    });
  }

  protected get showSelectContainer() {
    return ((_.toInteger(this.selection?.length) - _.toInteger(this.disabledSelection?.length)) && (this.showMakeFavAction || (this.selectedActions && this.multiSelect))) || false
  }

  /**
   * @ignore
   */
  ngOnInit(): void {


    fromEvent(window, 'resize')
      .pipe(
        takeUntil(this.destroyed$),
        throttleTime(500),
        debounceTime(500)
      )
      .subscribe(() => {
        //this.setFixedHeaderDetails();
        this.setFixedHeaderPosition();

        this.prepareVerticalShadows();

        this.setVisibilityIndexes();

        this.ref.markForCheck();
      });

  }

  /**
   * @ignore
   */
  ngAfterViewInit() {
    this.handleTableScroll();
    this.handleVisibleRowsOnScroll();

    if (!this.jsHoverDisabled) {
      this.handleRowHover();
    }

    setTimeout(() => {
      this.setVisibilityIndexes();
    }, 0);

    if (this.fixedHeader) {
      // sizes for fixed header should be calculated after the 200ms css transition on the main container
      setTimeout(() => {
        this.prepareVerticalShadows();
        this.initFixedHeader();
      }, 500);
    } else {
      setTimeout(() => {
        this.calculateTableWidth();
      }, 500);
    }
  }

  /**
   * @ignore
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['data']) {
      this.setVisibilityIndexes();
      this.dataChange$.next();
    }
    if (changes['columns'] || changes['userColumns']) {
      this.initColumns();
    }
    if (changes['selection']) {
      this.selectedRows.length = 0;
      this.selectedRow = undefined;

      changes['selection'].currentValue?.forEach((elem: any) => {
        const idx = this.data.indexOf(elem);

        if (idx !== -1) {
          if (this.multiSelect) {
            this.selectedRows.push(elem[this.dataPrimaryKey]);
          } else {
            this.selectedRow = elem[this.dataPrimaryKey];
          }
        }
      });

    }
  }

  /**
   * @ignore
   */
  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
    this.dataChange$.complete();
  }

  /**
   * @ignore
   */
  get visibleColumns() {
    return this.theColumns.filter((column) => column.visible == true);
  }


  /**
   * @ignore
   */
  initColumns() {
    if (Array.isArray(this.columns)) {
      if (this.userColumns === undefined || this.userColumns.length === 0) {
        this.initUserColumns();
      } else {
        this.buildTheColumns();
      }
    }
  }


  /**
   * calculate the sizes for the vertical shadows and if they are needed (if we have horizontal scroll);
   * after, set their initial position
   * @ignore
   */
  prepareVerticalShadows() {
    this.calculateTableWidth();
    this.checkHorizontalScrolling();
    this.calculateShadowsOffset();

    this.onTableScroll();
  }

  /**
   * check if we have horizontal scrolling or not
   * using the calculated table width and the tables wrapper width
   * @ignore
   */
  checkHorizontalScrolling() {
    const _wrapperWidth = this.wrapperLevel2?.nativeElement.getBoundingClientRect().width;

    if (this.tableWidth > (_wrapperWidth || 0)) {
      this.horizontalScrolling = true;
    } else {
      this.horizontalScrolling = false;
    }
  }

  /**
   * calculate the position of the vertical shadows (when there is horizontal scrolling)
   * if there are fixed/sticky columns, the offsets will be the widths of these columns; otherwise 0
   * the values will be set as css properties
   * @ignore
   */
  calculateShadowsOffset() {
    if (this.horizontalScrolling) {
      let _leftWidth: number = 0;
      let _rightWidth: number = 0;

      if (this.fixedFirstColumn) {
        const _firstColumn = this.theTable?.nativeElement.querySelector('thead tr th:first-of-type');
        if (_firstColumn) {
          _leftWidth = _firstColumn.getBoundingClientRect().width;
        }
      }
      if (this.fixedLastColumn) {
        let _lastColumn = this.theTable?.nativeElement.querySelector('thead tr th:last-of-type');

        // if helper cell is enabled, second from last column should be considered
        if (this.hasHelperCell) {
          _lastColumn = this.theTable?.nativeElement.querySelector('thead tr th:nth-last-of-type(2)');
        }
        if (_lastColumn) {
          _rightWidth = _lastColumn.getBoundingClientRect().width;
        }
      }

      this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-shadow-left-width", _leftWidth + 'px');
      this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-shadow-right-width", _rightWidth + 'px');
    }
  }

  /**
   * on table horizontal scroll:
   * - match the scroll position in the fixed/sticky header
   * - set the vertical shadows if necessary
   * @ignore
   */
  onTableScroll() {
    const scrollPosition: number | undefined = this.theTable?.nativeElement.scrollLeft;
    if (scrollPosition !== undefined) {
      if (this.theHeader?.nativeElement) {
        this.theHeader.nativeElement.scrollLeft = scrollPosition;
      }
      if (this.selectedActionsContainer?.nativeElement) {
        this.selectedActionsContainer.nativeElement.scrollLeft = scrollPosition;
      }
      this.setShadows(scrollPosition);
    }
  }

  /**
   * @ignore
   */
  handleTableScroll() {
    this.theTable?.nativeElement.addEventListener("scroll", () => {
      this.onTableScroll();
    })
  }

  /**
   * based on the scrollLeft value of the table, show/hide the shadows
   * there is a 5px tollerance
   * @ignore
   */
  setShadows(scrollLeft: number) {
    let _shadowLeft: boolean = false;
    let _shadowRight: boolean = false;

    if ((scrollLeft + this.wrapperLevel2?.nativeElement.getBoundingClientRect().width) < (this.tableWidth - 5)) {
      _shadowRight = true;
    }
    if (scrollLeft > 5) {
      _shadowLeft = true;
    }

    if (_shadowLeft !== this.tableShadowLeft) {
      this.tableShadowLeft = _shadowLeft;
      this.ref.markForCheck();
    }
    if (_shadowRight !== this.tableShadowRight) {
      this.tableShadowRight = _shadowRight;
      this.ref.markForCheck();
    }
  }

  /**
   * calculate the table width as the sum of the column widths
   * usefull to check if there is vertical scrolling or not
   * @ignore
   */
  calculateTableWidth() {
    const _headerCells = this.theTable?.nativeElement.querySelectorAll('thead tr th');
    let _tableWidth = 0;
    _headerCells?.forEach(cell => {
      _tableWidth += cell.getBoundingClientRect().width;
    });

    this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-width", _tableWidth + 'px');
    this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-wrapper-width", this.wrapperLevel2?.nativeElement.getBoundingClientRect().width + 'px');
    this.tableWidth = _tableWidth;
  }


  /**
   * @ignore
   */
  handleVisibleRowsOnScroll() {
    let _node: Window | HTMLTableElement = window;

    if (this.styleConfig().tableHeight && this.styleConfig().tableHeight !== 'auto' && this.theTable?.nativeElement) {
      _node = this.theTable?.nativeElement;
    }

    fromEvent(_node, 'scroll')
      .pipe(
        takeUntil(this.destroyed$),
        throttleTime(100),
        debounceTime(100)
      )
      .subscribe(() => {
        this.setVisibilityIndexes();
      });

  }

  /**
   * @ignore
   */
  setVisibilityIndexes() {
    const _endIndex = this.getEndIndex(this.visibilityEndIndex);
    const _startIndex = this.getStartIndex(this.visibilityStartIndex);

    if (this.visibilityEndIndex != _endIndex) {
      this.visibilityEndIndex = _endIndex;
      this.ref.markForCheck();
    }
    if (this.visibilityStartIndex != _startIndex) {
      this.visibilityStartIndex = _startIndex;
      this.ref.markForCheck();
    }
    //this.visibilityEndIndex = this.getEndIndex(this.visibilityEndIndex);
    //this.visibilityStartIndex = this.getStartIndex(this.visibilityStartIndex);
    //this.ref.markForCheck();
  }

  /**
   * returns the first cell in the row with the index
   * Note: index parameter (which comes from an array) is 0 based, so a +1 must be added in order to match the position in the DOM
   * @ignore
   */
  getChildElement(index: number) {
    return this.theTable?.nativeElement.querySelector('tbody tr:nth-child(' + (index + 1) + ') td');
  }

  /**
   * recursive function that returns the index of the first row that should be rendered
   * it checks the top positions of the current element (index) and the element placed at index + visibilityStep
   * @ignore
   */
  getStartIndex(index: number): number {
    const element = this.getChildElement(index); // this.theTable.nativeElement.querySelector('tbody tr:nth-child(' + index  +  ') td');
    const afterElement = this.getChildElement(index + this.visibilityStep); // this.theTable.nativeElement.querySelector('tbody tr:nth-child(' + (index + this.visibilityStep)  +  ') td');

    let _topLimit = 0;

    if (this.styleConfig().tableHeight && this.styleConfig().tableHeight !== 'auto') {
      _topLimit = this.wrapperLevel1?.nativeElement.getBoundingClientRect().top;
    }

    // if the afterElement (index + this.visibilityStep) is not visible, increase the current index (with the amount set by visibilityStep)
    if (afterElement) {
      if (afterElement.getBoundingClientRect().top < _topLimit) {
        return this.getStartIndex(index + this.visibilityStep);
      }
    }

    // if the current element is visible, decrease the current index (if possible with the amount set by visibilityStep, otherwise to 0)
    if (element) {
      if (element.getBoundingClientRect().top > _topLimit) {
        if (index > this.visibilityStep) {
          return this.getStartIndex(index - this.visibilityStep);
        } else {
          return 0;
        }
      }
    }
    // if the conditions above are met (current element above visible area and the afterElement visible) then return the current index
    return index;
  }

  /**
   * recursive function that returns the index of the last row that should be rendered
   * it checks the top positions of the current element (index) and the element placed at index - visibilityStep
   * @ignore
   */
  getEndIndex(index: number): number {
    const element = this.getChildElement(index); // this.theTable.nativeElement.querySelector('tbody tr:nth-child(' + index  +  ') td');
    const beforeElement = this.getChildElement(index - this.visibilityStep); //this.theTable.nativeElement.querySelector('tbody tr:nth-child(' + (index - this.visibilityStep)  +  ') td');

    let _bottomLimit = document.body.clientHeight;

    if (this.styleConfig().tableHeight && this.styleConfig().tableHeight !== 'auto') {
      _bottomLimit = this.wrapperLevel1?.nativeElement.getBoundingClientRect().bottom;
    }

    // if the beforeElement (index - this.visibilityStep) is not visible, decrease the current index (with the amount set by visibilityStep)
    if (beforeElement) {
      //if ((beforeElement.getBoundingClientRect().top - _bottomLimit) > 0) {
      if (beforeElement.getBoundingClientRect().top > _bottomLimit) {
        return this.getEndIndex(index - this.visibilityStep);
      }
    }

    // if the current element is visible, increase the current index (if possible with the amount set by visibilityStep, otherwise up to data.length); if necessary (reached the end of the loaded data) and available, request more data
    if (element) {
      //if ((element.getBoundingClientRect().top - _bottomLimit) < 0) {
      if (element.getBoundingClientRect().top < _bottomLimit) {
        if (index + this.visibilityStep < this.data.length) {
          return this.getEndIndex(index + this.visibilityStep);
        } else {
          if (this.data.length < this.total) {
            this.loadMore();
          }
          return this.data.length - 1;
        }
      }
    }
    // if the conditions above are met (current element bellow visible area and the beforeElement visible) then return the current index
    return index;
  }


  /**
   * calculate and set as css properties the position of the fixed/sticky header
   * @ignore
   */
  setFixedHeaderPosition() {
    const position = this.theTable?.nativeElement.getBoundingClientRect();

    if (position) {
      this.theHeader?.nativeElement.style.setProperty("--ig-table-header-offset-top", this.fixedHeaderOffsetTop + 'px');
      this.theHeader?.nativeElement.style.setProperty("--ig-table-header-offset-left", position.left + 'px');
      this.theHeader?.nativeElement.style.setProperty("--ig-table-header-offset-right", (document.body.clientWidth - (position.left + position.width)) + 'px');
      //this.theHeader.nativeElement.style.setProperty("--ig-table-header-width", position.width + 'px');

      this.selectedActionsContainer?.nativeElement.style.setProperty("--ig-table-header-offset-top", this.fixedHeaderOffsetTop + 'px');
      this.selectedActionsContainer?.nativeElement.style.setProperty("--ig-table-header-offset-left", position.left + 'px');
      this.selectedActionsContainer?.nativeElement.style.setProperty("--ig-table-header-offset-right", (document.body.clientWidth - (position.left + position.width)) + 'px');
      this.selectedActionsContainer?.nativeElement.style.setProperty("--ig-table-selected-height", this.selectedActionsContainerHeight + 'px');
    }
  }

  /**
   * set the values necessary for corect positioning of the header at page scroll
   * @ignore
   */
  setFixedHeaderDetails() {
    this.fixedHeaderHeight = this.theHeader?.nativeElement.getBoundingClientRect().height;
    this.selectedActionsContainerHeight = this.selectedActionsContainer?.nativeElement.getBoundingClientRect().height;

    if (this.viewportElement) {
      // @TODO: Hacky hack to make table fixed in cloud panel
      if (this.viewportElement === '#content') {
        this.viewportElement = 'main'
        this.fixedHeaderOffset = 0;
      }

      const _viewportElement = document.querySelector(this.viewportElement);
      if (_viewportElement) {
        const { paddingTop, marginTop } = getComputedStyle(_viewportElement);
        const { top } = _viewportElement.getBoundingClientRect();

        if (this.viewportElement === '.viewport') {
          this.fixedHeaderOffsetTop = parseFloat(paddingTop) + parseFloat(marginTop) + _.toInteger(this.fixedHeaderOffset) + (this.selectable ? this.selectedActionsContainerHeight : 0);
        } else {
          this.fixedHeaderOffsetTop = top - _.toInteger(paddingTop) + _.toInteger(this.fixedHeaderOffset) + (this.selectable ? this.selectedActionsContainerHeight : 0);
        }
      }
    }
  }

  /**
   * initialize fixed/sticky header
   * @ignore
   */
  initFixedHeader() {
    this.setFixedHeaderDetails();
    this.setFixedHeaderPosition();

    this.handleFixedHeaderOnScroll();
  }

  /**
   * change the fixed/sticky header positioning on page scroll
   * @ignore
   */
  handleFixedHeaderOnScroll() {
    fromEvent(window, 'scroll')
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        const tableTop = this.theTable?.nativeElement.getBoundingClientRect().top;
        const tableBottom = this.theTable?.nativeElement.getBoundingClientRect().bottom;

        // if we reached end of the table and there is more content on the page, let the fixed header leave the visible area
        if (((tableBottom || 0) - this.fixedHeaderHeight) <= this.fixedHeaderOffsetTop) {
          if (!this.fixedBottomTableHeader) {
            this.fixedBottomTableHeader = true;
            this.ref.markForCheck();
          }
        } else {
          if (this.fixedBottomTableHeader) {
            this.fixedBottomTableHeader = false;
            this.ref.markForCheck();
          }
        }

        // if the top of the table is above the point defined by the fixedHeaderOffsetTop, show the fixed/sticky header
        if ((tableTop || 0) <= this.fixedHeaderOffsetTop) {
          if (!this.fixedTableHeader) {
            this.fixedTableHeader = true;
            this.ref.markForCheck();
          }
        } else {
          if (this.fixedTableHeader) {
            this.fixedTableHeader = false;
            this.ref.markForCheck();
          }
        }
      });
  }



  /**
   * handle the (fake) hover on table rows
   * @ignore
   */
  handleRowHover() {
    // only listen for mousemove inside the table
    this.theTable?.nativeElement.addEventListener("mousemove", (e: MouseEvent) => {
      if (e.target) {
        const targetEl = _.get(e, 'target') as HTMLElement;
        if (targetEl && typeof (targetEl.closest) === 'function') {
          const closestTd = targetEl.closest('td');
          if (closestTd) {
            this.hoverRow(closestTd);
          } else {
            this.hoverRow();
          }
        }
      }
    });

    this.theTable?.nativeElement.addEventListener("mouseenter", (e) => {
      this.pointerOverTable = true;
      this.ref.markForCheck();
    });

    this.theTable?.nativeElement.addEventListener("mouseleave", (e) => {
      this.pointerOverTable = false;
      this.setHoverValues(0, 0);
      this.ref.markForCheck();
    });


    // on page scroll disable hover
    let _node: Window | HTMLTableElement = window;

    if (this.styleConfig().tableHeight && this.styleConfig().tableHeight !== 'auto' && this.theTable?.nativeElement) {
      _node = this.theTable?.nativeElement;
    }
    fromEvent(_node, 'scroll')
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        clearTimeout(this.hoverTimeout);

        this.rowHoverDisabled = true;
        this.setHoverValues(0, 0);

        this.hoverTimeout = window.setTimeout(() => {
          this.rowHoverDisabled = false;
          this.ref.markForCheck();
        }, 200);
      });
  }

  /**
   * set the values (top position and height) for the hover container to cell top position (relative to the table container) and cell height, if there is a cell, or 0 if there is no cell
   * @ignore
   */
  hoverRow(_cell?: HTMLElement) {
    if (_cell) {
      const top = Math.trunc(_cell.getBoundingClientRect().top - this.wrapperLevel2?.nativeElement.getBoundingClientRect().top);
      this.setHoverValues(top, _cell.getBoundingClientRect().height);
    } else {
      this.setHoverValues(0, 0);
    }
  }

  /**
   * if distinct, set the hover container values as css properties
   * @ignore
   */
  setHoverValues(topOffset: number, height: number) {
    if (this.hoverContainerTopOffset != topOffset) {
      this.hoverContainerTopOffset = topOffset;
      this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-hover-top", topOffset + 'px');
    }

    if (this.hoverContainerHeight != height) {
      this.hoverContainerHeight = height;
      this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-hover-height", height + 'px');
    }
  }


  /**
   * @ignore
   */
  initUserColumns() {
    this.userColumns = this.createUserColumns();
    this.buildTheColumns();
    this.changeColumns.emit(this.userColumns);
  }

  /**
   *
   * @ignore
   */
  createUserColumns() {
    const _columns: {
      columnId: string;
      // label: string;
      // type?: ColumnType;
      visible: boolean;
      position: number;
    }[] = [];
    this.columns?.forEach((column, index) => {
      _columns.push({
        'columnId': column.id,
        // 'label': column.headline,
        // 'type': column.type,
        'visible': column.preselected || false,
        'position': index
      })
    })
    _columns.sort((a, b) => a.position - b.position);
    return _columns;
  }

  /**
   * @ignore
   */
  private buildTheColumns() {
    this.theColumns = this.createTableColumns();
    this.gridifyTable();
  }

  /**
   * @ignore
   */
  private createTableColumns() {
    const _columns = [];
    let _settingsColumn: Column | undefined;

    this.columns?.forEach((column, index) => {
      if (column.type === ColumnType.SETTINGS) {
        _settingsColumn = { ...column };
        return true;
      }
      const _userColumn: UserColumn | undefined = this.userColumns?.find(({ columnId }) => columnId == column.id);
      _columns.push({
        ...column,
        'position': _userColumn?.position ?? index,
        'visible': _userColumn?.visible ?? column.preselected
      });
    });

    if (_settingsColumn) {
      _columns.push({
        ..._settingsColumn,
        'position': _columns.length,
        'visible': true
      });
    }

    _columns.sort((a, b) => a.position - b.position);

    return _columns;
  }

  /**
   * set as css properties the grid template and the colspan for empty rows and then set the table to display: grid;
   * @ignore
   */
  gridifyTable() {
    this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-grid-template-columns", this.buildGridTemplate());
    this.wrapperLevel1?.nativeElement.style.setProperty("--ig-table-grid-columns-number", this.getColumnsNumber());
    this.tableGrid = true;
  }

  /**
   * calculate and returns the number of non-sticky/fixed columns in the table;
   * used as cell span for the empty table rows or the load_more fake table
   * @ignore
   */
  getColumnsNumber() {
    let _columns = this.visibleColumns.length;
    if (this.selectable) {
      _columns += 1;
    }
    if (this.fixedFirstColumn) {
      _columns -= 1;
    }
    if (this.fixedLastColumn) {
      _columns -= 1;
    }
    return _columns;
  }

  /**
   * builds and returns the css grid template
   * @ignore
   */
  buildGridTemplate() {
    let template = '';

    if (this.selectable) {
      template += ' 50px';
    }
    this.visibleColumns.forEach(column => {
      if (column.width) {
        if (column.fixedWidth) {
          template += ' ' + column.width + 'px';
        } else {
          template += ' minmax(' + column.width + 'px, 1fr)';
        }
      } else {
        template += ' 1fr';
      }
    });
    if (this.hasHelperCell) {
      template += ' 0';
    }

    return template;
  }


  /**
   * @ignore
   */
  onClickCell(col: Column, row: any) {
    this.cellClick.emit({ col, row });
  }

  /**
   * @ignore
   */
  onSelectionChange() {
    this.selectionChange.emit(this.getSelectedData());
  }

  /**
   * @ignore
   */
  storeFavoritesBulk() {
    this.storeFavorites.emit(this.selection);
  }

  storeUnFavoritesBulk() {
    this.storeUnFavorites.emit(this.selection);
  }

  /**
   * @ignore
   */
  resetSelection() {
    if (this.allSelect) {
      this.selectAll = null;
    }

    this.selectedRows = [];
    this.onSelectionChange();
  }

  /**
   * @ignore
   */
  isRowBusy(row: any) {
    if (this.busyRows && _.find(this.busyRows, row)) {
      return true;
    }

    return false;
  }

  /**
   * @ignore
   */
  handleShowContextMenu(row: any) {
    if (!navigator.clipboard.writeText) {
      this.selectedContextMenuRowObjectUuid = null;
    } else {
      this.selectedContextMenuRowObjectUuid = row.object_uuid || null;
    }
  }

  /**
   * @ignore
   */
  isSelectionDisabled(row: any) {
    if (this.disabledSelection && _.find(this.disabledSelection, row)) {
      return true;
    }
    return false;
  }

  /**
   * @ignore
   */
  onHeaderColumnClick(col: Column) {
    if (col.type === ColumnType.SETTINGS) {
      this.settingsOverlay?.show();
    } else {
      this.onChangeSort(col);
    }
  }

  /**
   * @ignore
   */
  onChangeSort(col: Column) {
    if (col.type !== ColumnType.SETTINGS && col.sortable) {
      if (col.id === this.sort) {
        // change direction
        if (this.sortDirection === ColumnSortDirection.ASC) {
          this.sortDirection = ColumnSortDirection.DESC;
        } else {
          this.sortDirection = ColumnSortDirection.ASC;
        }
      }

      this.sort = col.id;
      this.changeSort.emit({
        columnId: this.sort,
        direction: this.sortDirection
      });
    }
    //this.ref.markForCheck();
  }

  /**
   * @ignore
   */
  loadMore() {
    if (!this.loadingNext) {
      this.loadNext.emit();
    }
  }

  /**
   * @ignore
   */
  onSelectAll(evt: TriStateCheckboxChangeEvent) {
    if (evt.value === true) {
      this.selectedRows = this.data.map(elem => _.get(elem, [this.dataPrimaryKey]));
    } else {
      this.selectedRows = this.disabledSelection?.length ? this.disabledSelection.map(elem => _.get(elem, [this.dataPrimaryKey])) : [];
    }
    this.onSelectionChange();
  }

  get checkboxState() {
    if (this.selectedRows?.length > 0 && this.selectedRows?.length !== this.total) {
      return false;
    } else return this.selectedRows?.length === this.total || null;
  }

  /**
   * checks if the row should be rendered or not;
   * returns true if the number of rows isn't that big or if the index (in array) is between the two visibility limits
   * @ignore
   */
  isRowVisible(index: number) {
    if (this.total > 50) {
      if (index >= this.visibilityStartIndex && index <= this.visibilityEndIndex) {
        return true;
      }
      return false;
    }
    return true;
  }

  /**
   * @ignore
   */
  getData(row: unknown, col: Column) {
    const val = _.get(row, col.dataSrc || '', col.dataDefault !== undefined ? col.dataDefault : '');

    if (this.search && this.searchBy === col.id) {
      return this.highlightPipe.transform(val, this.search);
    }

    if (_.isEmpty(val) && col.emptyValue) {
      return col.emptyValue;
    }
    return val;
  }

  /**
   * @ignore
   */
  private getFromData(id: string) {
    return this.data.find((item) => _.get(item, [this.dataPrimaryKey]) === id)
  }

  /**
   * returns the selected row(s) of the table
   * @ignore
   */
  private getSelectedData() {
    let selectedData: T[] = [];

    if (this.multiSelect) {
      if (this.selectedRows.length > 0) {
        this.selectedRows.forEach(id => {
          const item = this.getFromData(id);
          if (item) {
            selectedData.push(item);
          }
        });
      }
    } else if (this.selectedRow) {
      const item = this.getFromData(this.selectedRow);
      if (item) {
        selectedData = [item];
      }
    }
    return selectedData;
  }


  /**
   * handle the change in visibility of the table columns; reset vertical shadows at the end
   * @ignore
   */
  onChangeColVisibility(value: any, col: UserColumn) {
    this.buildTheColumns();
    this.changeColumns.emit(this.userColumns);

    setTimeout(() => {
      this.prepareVerticalShadows();
    }, 0);
  }

  /**
   * handle (drag and) drop of the table columns: move the dragged element into the new position, reset the positions and then rebuild the columns;
   * at the end, reset vertical shadows
   * @ignore
   */
  onColumnDrop(_event: CdkDragDrop<any, any, any>) {
    const event: CdkDragDrop<string[]> = _event;

    if (this.userColumns) {
      moveItemInArray(this.userColumns, event.previousIndex, event.currentIndex);
      this.userColumns.forEach((column, columnIndex) => {
        column.position = columnIndex;
      });
      this.userColumns.sort((a, b) => a.position - b.position);

      this.buildTheColumns();

      this.changeColumns.emit(this.userColumns);

      setTimeout(() => {
        this.prepareVerticalShadows();
      }, 0);
    }
  }

  onResetColumns() {
    this.initUserColumns();

    setTimeout(() => {
      this.prepareVerticalShadows();
    }, 0);
  }

  getColumn(columnId: string): Column {
    return _.find(this.columns, { id: columnId }) as Column;
  }

  getRowValue(row: T, key: string | number | string[]) {
    return _.get(row, key);
  }
}
